/**
 * MCP Handlers Index
 *
 * Exports all tool handlers and combines them into a unified handler map.
 */

export { readHandlers, type HandlerContext } from './readHandlers';
export { writeHandlers, type WriteHandlerContext } from './writeHandlers';
export { queryHandlers } from './queryHandlers';
export { validationHandlers } from './validationHandlers';
export {
  simulationHandlers,
  asyncSimulationHandlers,
  isSimulationControlTool,
  type SimulationHandlerContext,
} from './simulationHandlers';
export { docsHandlers } from './docsHandlers';

import { readHandlers } from './readHandlers';
import { writeHandlers } from './writeHandlers';
import { queryHandlers } from './queryHandlers';
import { validationHandlers } from './validationHandlers';
import { simulationHandlers, asyncSimulationHandlers } from './simulationHandlers';
import { docsHandlers } from './docsHandlers';
import type { MCPToolResult } from '../types';
import type { HandlerContext } from './readHandlers';
import type { WriteHandlerContext } from './writeHandlers';
import type { SimulationHandlerContext } from './simulationHandlers';

/**
 * Combined handler type that can be either read-only, write-capable, or simulation
 */
export type MCPHandler = (
  args: Record<string, unknown>,
  ctx: HandlerContext | WriteHandlerContext | SimulationHandlerContext
) => MCPToolResult;

/**
 * All handlers combined (excluding simulation handlers which need different context)
 */
export const allHandlers: Record<string, MCPHandler> = {
  ...readHandlers,
  ...writeHandlers,
  ...queryHandlers,
  ...validationHandlers,
  ...docsHandlers,
} as Record<string, MCPHandler>;

/**
 * Read-only handlers (safe operations)
 */
export const readOnlyHandlers: Record<string, MCPHandler> = {
  ...readHandlers,
  ...queryHandlers,
  ...validationHandlers,
  ...docsHandlers,
} as Record<string, MCPHandler>;

/**
 * Write handlers (modify model)
 */
export const writeOnlyHandlers: Record<string, MCPHandler> = {
  ...writeHandlers,
} as Record<string, MCPHandler>;

/**
 * Check if a tool is a write operation
 */
export function isWriteTool(toolName: string): boolean {
  return toolName in writeHandlers;
}

/**
 * Check if a tool is a simulation tool
 */
export function isSimulationTool(toolName: string): boolean {
  return toolName in simulationHandlers || toolName in asyncSimulationHandlers;
}

/**
 * Check if a tool is an async simulation tool
 */
export function isAsyncSimulationTool(toolName: string): boolean {
  return toolName in asyncSimulationHandlers;
}

/**
 * Check if a tool is a documentation tool
 */
export function isDocsTool(toolName: string): boolean {
  return toolName in docsHandlers;
}

/**
 * Get handler for a tool (excluding simulation handlers)
 */
export function getHandler(toolName: string): MCPHandler | undefined {
  return allHandlers[toolName];
}

/**
 * Get simulation handler for a tool (sync)
 */
export function getSimulationHandler(
  toolName: string
): ((args: Record<string, unknown>, ctx: SimulationHandlerContext) => MCPToolResult) | undefined {
  return simulationHandlers[toolName];
}

/**
 * Get async simulation handler for a tool
 */
export function getAsyncSimulationHandler(
  toolName: string
): ((args: Record<string, unknown>, ctx: SimulationHandlerContext) => Promise<MCPToolResult>) | undefined {
  return asyncSimulationHandlers[toolName];
}
